<template>
  <v-dialog v-model="isOpened" :persistent="editable" @update:model-value="onCancel">
    <v-card>
      <v-toolbar color="teal">
        <v-toolbar-title v-if="!props.modelValue.id && props.modelValue.pinned && editable">위치 등록</v-toolbar-title>
        <v-toolbar-title v-else-if="props.modelValue.id && props.modelValue.pinned && editable">위치 수정</v-toolbar-title>
        <v-toolbar-title v-else-if="props.modelValue.pinned && !editable">위치 보기</v-toolbar-title>
        <v-toolbar-title v-else-if="!props.modelValue.id && !props.modelValue.pinned && editable">메모 등록</v-toolbar-title>
        <v-toolbar-title v-else-if="props.modelValue.id && !props.modelValue.pinned && editable">메모 등록</v-toolbar-title>
        <v-toolbar-title v-else-if="!props.modelValue.pinned && !editable">메모 보기</v-toolbar-title>
        <v-toolbar-items v-if="props.modelValue.canEdit && !editable">
          <v-btn icon="mdi-file-edit" @click.stop="editable = true"></v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text v-if="!props.modelValue.pinned">
        <v-text-field density="compact" :readonly="!editable" hide-details label="제목(Subject)" v-model="subject"></v-text-field>
      </v-card-text>
      <v-card-text>
        <v-textarea :readonly="!editable" class="flex-1-1" density="compact" hide-details label="내용(Content)" v-model="content"></v-textarea>
      </v-card-text>
      <v-card-text :key="file.seq" v-for="(file, fileIndex) in attachments">
        <v-hover v-slot="{ isHovering, props }">
          <v-card variant="flat" v-bind="props">
            <v-img class="border rounded" cover :src="file.src"></v-img>
            <div v-if="isHovering && editable"
                 style="position: absolute;top: 0;right: 0;bottom: 0;left: 0;display: flex;justify-content: center;align-items: center;background: #0006;">
              <v-btn variant="flat" icon="mdi-close" @click.stop="onDeleteFile(file, fileIndex)"></v-btn>
            </div>
          </v-card>
        </v-hover>
      </v-card-text>
      <v-card-text v-if="editable">
        <v-sheet
            :class="['file-upload', isDragged && 'dragged' || '']"
            height="100" color="grey-lighten-2" rounded
            @dragenter="onDragenter"
            @dragover.prevent="onDragover"
            @dragleave="onDragleave"
            @drop.prevent="onDrop"
            @click="onClick"
        >
          <span class="text-body-2">여기에 이미지 파일을 끌어다 놓으세요.</span>
        </v-sheet>
        <input type="file" accept="image/*" class="d-none" ref="fileInput" @change="onFileChange" multiple>
      </v-card-text>
      <v-card-actions v-if="editable">
        <v-btn @click.stop="onDelete" v-if="props.modelValue.id">삭제</v-btn>
        <v-spacer></v-spacer>
        <v-btn @click.stop="onCancel">취소</v-btn>
        <v-btn variant="tonal" @click.stop="onOk">저장</v-btn>
      </v-card-actions>
      <v-card-actions v-else>
        <v-spacer></v-spacer>
        <v-btn @click.stop="onConfirm">확인</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>

import {computed, inject, ref} from 'vue';
import * as uuid from 'uuid';
import {HttpStatusCode} from "axios";

const props = defineProps(['modelValue']);
const emit = defineEmits(['update:modelValue', 'onDelete', 'onCancel', 'onOk', 'onConfirm']);

const axios = inject('axios');
const network = inject('network');

const editable = ref(!!props.modelValue.editable);

const isOpened = ref(true);
const isDragged = ref(false);
const fileInput = ref(null);
const subject = ref((props.modelValue.subject));
const content = ref(props.modelValue.content);
const attachments = ref((props.modelValue.attachments || []).map(attachment => ({
  ...attachment,
  seq: attachment.id
})));

const onClick = () => {
  fileInput.value && fileInput.value.click();
};

const onDragenter = event => isDragged.value = true;

const onDragleave = event => isDragged.value = false;

const onDragover = event => {};

const onDrop = event => {
  isDragged.value = false
  const files = Array.from(event.dataTransfer.files);
  addAttachments(files);
};

const onFileChange = event => {
  const files = Array.from(event.target.files);
  addAttachments(files);
};

const addAttachments = files => {
  (async () => {
    const imageFiles = files.filter(({type}) => type.startsWith('image/'));
    const funcs = imageFiles.map(file => () => readFile(file));
    const sources = await Promise.all(funcs.map(func => func()));
    const merged = imageFiles.map((file, index) => ({
      seq: uuid.v1(),
      src: sources[index],
      overlay: false,
      file,
    }));
    attachments.value = [...attachments.value, ...merged];
  })();
};

const readFile = file => new Promise(r => {
  const reader = new FileReader();
  reader.onload = e => r(e.target.result);
  reader.readAsDataURL(file);
});

const onDeleteFile = (file, index) => attachments.value = attachments.value.filter((f, i) => i !== index);

const onDelete = () => {
  if (!confirm('삭제하시겠습니까?')) return;
  (async () => {
    try {
      const response = await axios.delete(`/api/post/${props.modelValue.id}`);
      await new Promise(r => network.useResponseChain()
          .use((response, next) => {
            if (!response || HttpStatusCode.Ok !== response.status || 0 !== response.data.code)
              return next && next();
            r(true)
          })
          .use(() => r(false))
          .run(response)
      );
    } catch (error) {
      console.log(error);
    }

    isOpened.value = false;
    emit('onDelete');
  })();
};

const touched = computed(() => JSON.stringify([
  props.modelValue.subject,
  props.modelValue.content,
  props.modelValue.attachments
]) !== JSON.stringify([
  subject.value,
  content.value,
  attachments.value
]));

const onCancel = () => {
  if (props.modelValue.id && editable.value) {
    if (touched.value && !confirm('변경된 내용이 있습니다. 취소하시겠습니까?'))
      return;

    subject.value = props.modelValue.subject;
    content.value = props.modelValue.content;
    attachments.value = props.modelValue.attachments;

    editable.value = false;
    return;
  }

  isOpened.value = false;
  emit('onCancel');
};

const onOk = () => {
  const _subject = subject.value && subject.value.trim() || '';
  const _content = content.value && content.value.trim() || '';

  if (!props.modelValue.pinned && !_subject) return alert('제목을 입력하세요.');
  else if (!_content) return alert('내용을 입력하세요.');

  (async () => {
    const formData = new FormData();
    formData.append('type', props.modelValue.pinned ? 'pinned' : 'memo');
    formData.append('subject', _subject);
    formData.append('content', _content);
    formData.append('deviceJson', JSON.stringify(props.modelValue.device));

    formData.append('attachmentsJson',
        JSON.stringify(attachments.value.map(attachment => attachment.file || attachment))
    );
    attachments.value.filter(({file}) => file).forEach(({file}) => formData.append('files', file, file.name));

    const response = await (async () => {
      if (props.modelValue.id) {
        formData.append('id', props.modelValue.id);
        return await axios.put(`/api/post/${props.modelValue.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      return await axios.post('/api/post', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    })();

    isOpened.value = false;
    emit('onOk', {
      subject: subject.value,
      content: content.value,
      files: attachments.value,
    });
  })().catch(error => console.log(error));
};

const onConfirm = () => {
  isOpened.value = false;
  emit('onConfirm');
};

</script>

<style scoped>
.file-upload {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &.dragged {
    border: 1px dashed powderblue;
    opacity: .6;
  }
}
</style>
